import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { GridWithSearch, SecondaryData } from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { ViewRawAuditLogDialog } from './dialogs/ViewRawAuditLogDialog';

import { useGetAllSuppliersQuery } from 'store/api/referenceApi';
import { useGetTreatmentAuditLogsQuery } from 'store/api/treatmentAuditApi';

const initialSortProperties = [{ id: 'timestamp', desc: true }];

const getColumns = (setRawAuditLogDialogOpen) => [
    {
        accessorKey: 'title',
        header: 'Event Type',
        size: 40,
    },
    {
        accessorKey: 'description',
        header: 'Event Description',
        size: 120,
    },
    {
        accessorKey: 'hasEventJSON',
        header: () => <Icon icon={IconNames.CODE} />,
        enableSorting: false,
        size: 5,
        cell: ({ getValue, row }) => {
            const { logId, title, timestamp } = row.original;
            return getValue() ? (
                <Icon
                    icon={IconNames.CODE}
                    intent={Icon.Intent.DEFAULT}
                    tooltipContent="View Raw Audit Log"
                    onClick={() =>
                        setRawAuditLogDialogOpen({
                            logId,
                            title,
                            timestamp,
                        })
                    }
                />
            ) : null;
        },
    },
    {
        accessorKey: 'reason',
        header: () => <Icon icon={IconNames.MANUALLY_ENTERED_DATA} />,
        enableSorting: false,
        size: 5,
        cell: ({ getValue, row }) => {
            const reason = getValue();
            const { source } = row.original;
            return reason ? (
                <Icon
                    icon={IconNames.MANUALLY_ENTERED_DATA}
                    intent={Icon.Intent.DEFAULT}
                    tooltipContent={
                        <div>
                            <div>
                                <span className="font-bold">Reason:</span> {reason}
                            </div>
                            {source && (
                                <div>
                                    <span className="font-bold">Source:</span> {source}
                                </div>
                            )}
                        </div>
                    }
                />
            ) : null;
        },
    },
    {
        accessorKey: 'upn',
        header: 'User',
        size: 40,
    },
    {
        accessorKey: 'timestamp',
        header: 'Date/Time',
        size: 40,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
];

const replaceSupplier = (supplierMap, value) =>
    value?.replace(/\$SNPI:(\d{10})\$/, (_match, npi) => {
        const supplierName = supplierMap[npi]?.name;
        return supplierName ? `${supplierName} (${npi})` : npi;
    });

export const TreatmentAuditHistory = ({ treatmentId }) => {
    const [rawAuditLogDialogOpen, setRawAuditLogDialogOpen] = useState();
    const { data: auditLogEntries = [], isLoading } = useGetTreatmentAuditLogsQuery({
        treatmentId,
    });
    const { data: allSuppliersData } = useGetAllSuppliersQuery();

    const formattedAuditLogEntries = auditLogEntries.map((entry) => ({
        ...entry,
        upn: entry.upn ?? 'system',
        description: replaceSupplier(allSuppliersData?.supplierMap ?? {}, entry.description),
    }));

    return (
        <>
            <ViewRawAuditLogDialog
                isOpen={!!rawAuditLogDialogOpen}
                onClose={() => setRawAuditLogDialogOpen()}
                logId={rawAuditLogDialogOpen?.logId}
                title={rawAuditLogDialogOpen?.title}
                timestamp={rawAuditLogDialogOpen?.timestamp}
            />
            <GridWithSearch
                enableSorting
                data={formattedAuditLogEntries}
                isLoading={isLoading}
                sortProperties={initialSortProperties}
                columns={getColumns(setRawAuditLogDialogOpen)}
            />
        </>
    );
};

TreatmentAuditHistory.propTypes = {
    treatmentId: PropTypes.string.isRequired,
};
